/**
 * ライブ受信データ（航跡など）を端末内（IndexedDB）に残す。
 *
 * ★ なぜ要るか（2026-10-09、docs/08 §191）
 *   切断→接続はメモリに残したデータで速くなったが（§190）、スマホでは
 *   アプリ（PWA）が裏で終了しやすく、開き直した直後の接続は全点を
 *   受け取り直していた。ここに残しておけば、開き直してもすぐ航跡を
 *   表示し、ブリッジには続き（`since`以降）だけを頼める。
 *
 * 航跡は gv-core の`packFixes`で詰めた形で保存する（7機×1時間で約0.6MB。
 * FixSampleのままより書き込み・読み込みとも軽い）。
 *
 * IndexedDBが無い・使えない環境（Node のテスト、プライベートブラウズ等）
 * では`indexedDbTrackCache()`が null を返し、保存しないだけで動作は続く。
 */

import type { CircleArea, FixSample, MarkPoint, PackedTrack } from '@gv4d/core';

export interface StoredTrackCache {
  /** 接続先（ブリッジのURL）。これが保存の鍵 */
  baseUrl: string;
  generation: string;
  seq: number;
  /** 保存した時刻（epoch ms）。古すぎるものは使わない */
  savedAt: number;
  terminals: Array<{ terminalId: string; latest: FixSample; latestIsPlausible: boolean; packed: PackedTrack }>;
  circles: Array<{ terminalId: string; circles: CircleArea[] }>;
  marks: Array<{ terminalId: string; marks: MarkPoint[] }>;
}

export interface TrackCachePersistence {
  load(baseUrl: string): Promise<StoredTrackCache | null>;
  save(cache: StoredTrackCache): Promise<void>;
}

const DB_NAME = 'gv4d-live-cache';
const STORE = 'tracks';
/** 「全部消して読み込み直す」（pwa.ts）からも消せるよう公開しておく */
export const TRACK_CACHE_DB_NAME = DB_NAME;

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: 'baseUrl' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    // 別タブが古い版で開いたままのとき。待ち続けないよう失敗扱いにする
    req.onblocked = () => reject(new Error('IndexedDB blocked'));
  });
}

/** IndexedDBに保存する実装。使えない環境では null */
export function indexedDbTrackCache(): TrackCachePersistence | null {
  if (typeof indexedDB === 'undefined') return null;
  let dbPromise: Promise<IDBDatabase> | null = null;
  const db = (): Promise<IDBDatabase> => {
    dbPromise ??= openDb().catch((e: unknown) => {
      dbPromise = null; // 次回は開き直す
      throw e;
    });
    return dbPromise;
  };
  return {
    async load(baseUrl) {
      const d = await db();
      const row = await promisify(d.transaction(STORE, 'readonly').objectStore(STORE).get(baseUrl));
      return (row as StoredTrackCache | undefined) ?? null;
    },
    async save(cache) {
      const d = await db();
      const tx = d.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(cache);
      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      });
    },
  };
}
